import { useState } from 'react'
import { toast } from 'react-toastify'
import { useJobGroups } from '@/api/queries/job-group-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { usePeople } from '@/api/queries/people-queries'
import { CommitmentMatrixMultiSelector } from '@/components/administration/commitment-matrix/commitment-matrix-multi-selector'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import type { CommitmentMatrix } from '@/types'

export interface CommitmentMatrixFormValues {
	name: string
	monthlyTarget: number
	branchIds: string[]
	crIds: string[]
	jobGroupIds: string[]
	personIds: string[]
}

interface CommitmentMatrixFormProps {
	mode: 'create' | 'edit'
	initial?: CommitmentMatrix
	onSave: (values: CommitmentMatrixFormValues) => void
	onClose: () => void
	isSaving?: boolean
}

export function CommitmentMatrixFormContent({
	mode,
	initial,
	onSave,
	onClose,
	isSaving = false,
}: CommitmentMatrixFormProps) {
	const [name, setName] = useState(initial?.name ?? '')
	const [monthlyTarget, setMonthlyTarget] = useState(
		initial ? String(initial.monthlyTarget) : '',
	)
	const [branchIds, _setBranchIds] = useState<string[]>(
		initial?.branchIds ?? [],
	)
	const [crIds, setCrIds] = useState<string[]>(initial?.crIds ?? [])
	const [jobGroupIds, setJobGroupIds] = useState<string[]>(
		initial?.jobGroupIds ?? [],
	)
	const [personIds, setPersonIds] = useState<string[]>(initial?.personIds ?? [])

	const { data: branches, isLoading: isLoadingBranches } = useOrganization()
	const { data: jobGroups, isLoading: isLoadingJobGroups } = useJobGroups()
	const { data: people, isLoading: isLoadingPeople } = usePeople(true)

	const personOptions = (people ?? []).map((p) => ({
		id: p.id,
		name: p.name,
		description: p.crName,
	}))
	const crOptions = (branches ?? []).flatMap((b) =>
		b.crs.map((c) => ({ id: c.id, name: c.name, description: b.name })),
	)
	const jobGroupOptions = (jobGroups ?? []).map((g) => ({
		id: g.id,
		name: g.name,
		description: g.jobRoles.join(', '),
	}))

	const handleSave = () => {
		if (!name.trim()) {
			toast.error('Informe um nome para a matriz.')
			return
		}
		const target = Number(monthlyTarget)
		if (!monthlyTarget || Number.isNaN(target) || target <= 0) {
			toast.error('Informe uma meta mensal válida (maior que zero).')
			return
		}

		onSave({
			name: name.trim(),
			monthlyTarget: target,
			branchIds,
			crIds,
			jobGroupIds,
			personIds,
		})
	}

	return (
		<div className="flex flex-col gap-5 px-1">
			<Field>
				<FieldLabel htmlFor="matrix-name">Nome da matriz</FieldLabel>
				<FieldContent>
					<Input
						id="matrix-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="Ex: Meta Padrão - Técnicos de Segurança"
						maxLength={120}
					/>
				</FieldContent>
			</Field>

			<Field>
				<FieldLabel htmlFor="matrix-target">
					Meta mensal (inspeções/mês)
				</FieldLabel>
				<FieldContent>
					<Input
						id="matrix-target"
						type="number"
						min={1}
						value={monthlyTarget}
						onChange={(e) => setMonthlyTarget(e.target.value)}
						placeholder="Ex: 6"
					/>
				</FieldContent>
			</Field>

			<Separator className="bg-border/60" />

			<div className="flex flex-col gap-3">
				<p className="text-xs text-muted-foreground">
					Vincule esta matriz a Filiais, CRs, Grupos de Cargo e/ou Pessoas
					específicas. Quando um colaborador se enquadra em matrizes de mais de
					um nível, vale a ordem de precedência{' '}
					<strong>Pessoal &gt; Grupo &gt; CR &gt; Filial</strong> — o nível mais
					específico vence, mesmo que o valor de um nível mais genérico seja
					maior. Se o nível mais específico não tiver matriz ativa, cai para o
					próximo.
				</p>

				<CommitmentMatrixMultiSelector
					title="Pessoas"
					options={personOptions}
					selected={personIds}
					onChange={setPersonIds}
					searchPlaceholder="Buscar pessoa..."
					emptyMessage="Nenhuma pessoa encontrada"
					loading={isLoadingPeople}
				/>

				<CommitmentMatrixMultiSelector
					title="CRs"
					options={crOptions}
					selected={crIds}
					onChange={setCrIds}
					searchPlaceholder="Buscar CR..."
					emptyMessage="Nenhum CR encontrado"
					loading={isLoadingBranches}
				/>

				<CommitmentMatrixMultiSelector
					title="Grupos de Cargo"
					options={jobGroupOptions}
					selected={jobGroupIds}
					onChange={setJobGroupIds}
					searchPlaceholder="Buscar grupo..."
					emptyMessage="Nenhum grupo de cargo encontrado"
					loading={isLoadingJobGroups}
				/>
			</div>

			<Separator className="bg-border/60" />

			<div className="flex gap-3 pb-1">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					className="flex-1"
					disabled={isSaving}
				>
					Cancelar
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					disabled={isSaving}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{isSaving
						? 'Salvando...'
						: mode === 'create'
							? 'Criar matriz'
							: 'Salvar alterações'}
				</Button>
			</div>
		</div>
	)
}
